import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import mobileCheck from 'components/Dashboard/helpers/mobileCheck';
import Loading from 'components/Login/Loading';
import useShareDocument from 'api/hooks/useShareDocument';
import SharedDocumentPreview, { type SharedDocumentData } from 'components/ShareDocument/SharedDocumentPreview';

const getDocumentFaviconUrl = () => `${import.meta.env.VITE_APP_CDNURL}/images/document-scope-favicon.svg`;

const ShareDocumentV2Page = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const { getSharedDocumentV2 } = useShareDocument();
  const [mainDocument, setMainDocument] = useState<SharedDocumentData>({});

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [notFound, setNotFound] = useState<boolean>(false);

  const loadSharedDocument = useRef<((signal: AbortSignal) => Promise<void>) | null>(null);

  loadSharedDocument.current = async (signal) => {
    try {
      if (!uuid) {
        setNotFound(true);
        setIsLoading(false);
        document.title = 'Document not found';
        return;
      }

      setIsLoading(true);
      setNotFound(false);
      const response = await getSharedDocumentV2({ uuid, signal });
      if (response !== undefined) {
        const sharedDocument: SharedDocumentData = {
          documentUrl: response?.documentUrl,
          fileName: response?.fileName,
          contentType: response?.contentType,
          backgroundColor: response?.backgroundColor,
          barBackgroundColor: response?.barBackgroundColor,
          organisationLogo: response?.organisationLogo,
        };

        if (sharedDocument?.documentUrl) {
          setMainDocument(sharedDocument);
          setNotFound(false);

          if (sharedDocument.fileName) {
            document.title = sharedDocument.fileName;
          }

          const documentFaviconUrl = sharedDocument.organisationLogo || getDocumentFaviconUrl();
          document.querySelector('link[rel="icon"]')?.setAttribute('href', documentFaviconUrl);
          document.querySelector('link[rel="apple-touch-icon"]')?.setAttribute('href', documentFaviconUrl);
        } else {
          setNotFound(true);
          document.title = 'Document not found';
        }
        setIsLoading(false);
      }
    } catch {
      // apiClient's error interceptor already shows a toast
    }
  };

  useEffect(() => {
    document.getElementById('policymogul-cookie-item')?.remove();

    if ((window as any).Intercom) {
      (window as any).Intercom('update', { hide_default_launcher: true });
    }
    const controller = new AbortController();
    loadSharedDocument.current?.(controller.signal);

    return () => {
      controller.abort('Share document component unmounted.');
      if ((window as any).Intercom) {
        (window as any).Intercom('update', { hide_default_launcher: mobileCheck() });
      }
    };
  }, []);

  if (isLoading || notFound || !mainDocument.documentUrl) {
    return (
      <div style={{ minHeight: '100vh' }}>
        <div
          className='share-document-page main-content-wrapper px-3 px-lg-5 pt-5 mx-auto'
          style={{ maxWidth: '920px' }}
        >
          {isLoading ? (
            <Loading />
          ) : (
            <div className='box-top-rounded bg-white p-4 p-lg-5 mx-auto' style={{ maxWidth: '720px' }}>
              <h1 className='title-h3-bold mb-3'>Document not found</h1>
              <p className='paragraph-general main-subtle-text mb-0'>
                This share link is invalid, expired, or no longer available.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return <SharedDocumentPreview document={mainDocument} />;
};

export default ShareDocumentV2Page;
